import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Device, type Call } from '@twilio/voice-sdk';
import { api } from '../api';
import { useAuth } from './AuthContext';

export type VoiceStatus = 'off' | 'connecting' | 'ready' | 'not-configured' | 'error';
export type CallState = 'idle' | 'connecting' | 'ringing' | 'in-call' | 'incoming';

interface VoiceCtx {
  status: VoiceStatus;
  edge: string | null;
  callState: CallState;
  activeWho: string;
  error: string | null;
  inputs: MediaDeviceInfo[];
  outputs: MediaDeviceInfo[];
  inId: string;
  outId: string;
  outputSupported: boolean;
  placeCall: (to: string) => void;
  callJob: (eventId: string, displayName: string) => void;
  hangUp: () => void;
  answer: () => void;
  decline: () => void;
  chooseMic: (id: string) => void;
  chooseSpeaker: (id: string) => void;
}

const Ctx = createContext<VoiceCtx | null>(null);
export const useVoice = () => useContext(Ctx);

/** A generated ringback tone (so the caller hears the phone "ringing" while an
 *  outbound call connects). Australian double-ring cadence (400 + 425 Hz, two
 *  ~0.4s bursts then a pause), looped via the Web Audio API — no asset to bundle.
 *  Returns start()/stop(); safe no-op if audio is blocked. */
function createRingback() {
  let ctx: AudioContext | null = null;
  let gain: GainNode | null = null;
  let osc1: OscillatorNode | null = null;
  let osc2: OscillatorNode | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;

  const start = () => {
    if (ctx) return; // already ringing
    try {
      const AC: typeof AudioContext | undefined = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AC) return;
      ctx = new AC();
      ctx.resume?.();
      gain = ctx.createGain();
      gain.gain.value = 0;
      gain.connect(ctx.destination);
      osc1 = ctx.createOscillator(); osc1.type = 'sine'; osc1.frequency.value = 400; osc1.connect(gain); osc1.start();
      osc2 = ctx.createOscillator(); osc2.type = 'sine'; osc2.frequency.value = 425; osc2.connect(gain); osc2.start();
      const burst = (at: number) => {
        if (!gain) return;
        gain.gain.setValueAtTime(0.0001, at);
        gain.gain.exponentialRampToValueAtTime(0.12, at + 0.02);
        gain.gain.setValueAtTime(0.12, at + 0.38);
        gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.4);
      };
      const cycle = () => {
        if (!ctx) return;
        const t = ctx.currentTime;
        burst(t + 0.05); // brrr
        burst(t + 0.65); // brrr
      };
      cycle();
      timer = setInterval(cycle, 3000); // ~2s pause between double-rings
    } catch { /* audio unavailable — silent */ }
  };

  const stop = () => {
    if (timer) { clearInterval(timer); timer = null; }
    try { osc1?.stop(); } catch { /* ignore */ }
    try { osc2?.stop(); } catch { /* ignore */ }
    try { ctx?.close(); } catch { /* ignore */ }
    ctx = null; gain = null; osc1 = null; osc2 = null;
  };

  return { start, stop };
}

/** Normalise a dialled number to E.164, assuming Australia when no country code
 *  is given — so staff can type `0404…`, `404…`, or `04 04…` without the +61. */
export function normalizeDialled(raw: string): string {
  const s = (raw || '').replace(/[^\d+]/g, '');
  if (!s) return '';
  if (s.startsWith('+')) return s;                        // already E.164
  if (s.startsWith('0011')) return '+' + s.slice(4);      // AU intl dial prefix
  if (/^0\d{9}$/.test(s)) return '+61' + s.slice(1);      // 0404xxxxxx / 0x xxxx xxxx
  if (/^61\d{9}$/.test(s)) return '+' + s;                // 61404xxxxxx
  if (/^\d{9}$/.test(s)) return '+61' + s;                // 404xxxxxx (no leading 0)
  return '+' + s;                                         // else assume country code present
}

/**
 * App-wide softphone. Registers one Twilio Voice client for the whole session so
 * incoming calls ring on any page (not just the Phone tab), and renders a global
 * incoming/active-call bar. The Phone tab (keypad/recents) drives this via the
 * hook. Only active for admins; dormant if Voice isn't configured.
 */
export function VoiceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  // Admins and contractors both register a softphone. Contractors are outbound-
  // only (unique identity → the office's inbound calls never ring them); they
  // use it to call a job's customer via the masked "Call" button.
  const isStaff = user?.role === 'ADMIN' || user?.role === 'CONTRACTOR';

  const [status, setStatus] = useState<VoiceStatus>('off');
  const [edge, setEdge] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [callState, setCallState] = useState<CallState>('idle');
  const [activeWho, setActiveWho] = useState('');
  const [inputs, setInputs] = useState<MediaDeviceInfo[]>([]);
  const [outputs, setOutputs] = useState<MediaDeviceInfo[]>([]);
  const [inId, setInId] = useState('');
  const [outId, setOutId] = useState('');
  const [outputSupported, setOutputSupported] = useState(false);
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const incomingRef = useRef<Call | null>(null);
  const ringbackRef = useRef<ReturnType<typeof createRingback> | null>(null);
  if (!ringbackRef.current) ringbackRef.current = createRingback();

  // Play the ringback tone while an outbound call is connecting/ringing; stop it
  // once it's answered, hung up, or on any other state. Covers every call in the
  // app since they all run through this context.
  useEffect(() => {
    const rb = ringbackRef.current;
    if (!rb) return;
    if (callState === 'connecting' || callState === 'ringing') rb.start();
    else rb.stop();
  }, [callState]);
  useEffect(() => () => ringbackRef.current?.stop(), []);

  const cleanup = () => {
    callRef.current = null;
    incomingRef.current = null;
    setActiveWho('');
    setCallState('idle');
  };

  useEffect(() => {
    if (!isStaff) return;
    setStatus('connecting');
    let cancelled = false;
    let device: Device | null = null;
    (async () => {
      try {
        const { token } = await api.getVoiceToken();
        if (cancelled) return;
        // Inbound calls to this number are processed in Sydney (au1), so the token
        // is minted with region au1 (needs an au1 API Key) and the device pins the
        // Sydney edge — client registers in au1 where <Dial><Client> resolves it.
        // With a us1 client the inbound dial finds nothing and returns no-answer.
        device = new Device(token, { codecPreferences: ['opus', 'pcmu'] as any, logLevel: 'error' as any, edge: 'sydney' });
        deviceRef.current = device;
        const refreshDevices = () => {
          const audio: any = device?.audio;
          if (!audio) return;
          try {
            setInputs(Array.from((audio.availableInputDevices?.values?.() ?? []) as Iterable<MediaDeviceInfo>));
            setOutputs(Array.from((audio.availableOutputDevices?.values?.() ?? []) as Iterable<MediaDeviceInfo>));
            setOutputSupported(!!audio.isOutputSelectionSupported);
          } catch { /* ignore */ }
        };
        device.on('registered', () => { if (!cancelled) { setStatus('ready'); setEdge(((device as any)?.edge ?? null)); refreshDevices(); } });
        device.on('error', (e: any) => setError(e?.message || 'Voice error'));
        device.on('incoming', (call: Call) => {
          incomingRef.current = call;
          const from = (call.parameters?.From as string) || '';
          setActiveWho(from || 'Incoming call');
          setCallState('incoming');
          // Resolve the caller to a contact name so the ringing bar shows who it is.
          if (from && isAdmin) {
            api.lookupVoiceCaller(from)
              .then((r) => { if (r?.name && incomingRef.current === call) setActiveWho(`${r.name} · ${from}`); })
              .catch(() => { /* keep the number */ });
          }
          call.on('disconnect', cleanup);
          call.on('cancel', cleanup);
          call.on('reject', cleanup);
        });
        device.on('tokenWillExpire', async () => {
          try { const t = await api.getVoiceToken(); device?.updateToken(t.token); } catch { /* ignore */ }
        });
        (device.audio as any)?.on?.('deviceChange', refreshDevices);
        await device.register();
      } catch (e: any) {
        if (cancelled) return;
        if ((e?.message || '').toLowerCase().includes('not configured')) setStatus('not-configured');
        else { setStatus('error'); setError(e?.message || 'Voice unavailable'); }
      }
    })();
    return () => { cancelled = true; try { device?.destroy(); } catch { /* ignore */ } deviceRef.current = null; };
  }, [isStaff]);

  const placeCall = async (to: string) => {
    const device = deviceRef.current;
    const dest = normalizeDialled(to);
    if (!device || !dest || callState !== 'idle') return;
    try {
      setActiveWho(dest);
      setCallState('connecting');
      // Resolve the dialled number to a contact name for the call bar.
      api.lookupVoiceCaller(dest)
        .then((r) => { if (r?.name) setActiveWho((cur) => (cur === dest ? `${r.name} · ${dest}` : cur)); })
        .catch(() => { /* keep the number */ });
      const call = await device.connect({ params: { To: dest, dir: 'out' } });
      callRef.current = call;
      call.on('ringing', () => setCallState('ringing'));
      call.on('accept', () => setCallState('in-call'));
      call.on('disconnect', cleanup);
      call.on('cancel', cleanup);
      call.on('error', (e: any) => { setError(e?.message || 'Call error'); cleanup(); });
    } catch (e: any) {
      setError(e?.message || 'Could not start the call');
      cleanup();
    }
  };
  // Call a job's customer by event id only — the number is resolved server-side
  // in the outbound webhook, so it's never sent to (or shown in) this client.
  // The call bar shows just the customer's name.
  const callJob = async (eventId: string, displayName: string) => {
    const device = deviceRef.current;
    if (!device || !eventId || callState !== 'idle') return;
    try {
      setActiveWho(displayName || 'Customer');
      setCallState('connecting');
      const call = await device.connect({ params: { callJob: eventId, dir: 'out' } });
      callRef.current = call;
      call.on('ringing', () => setCallState('ringing'));
      call.on('accept', () => setCallState('in-call'));
      call.on('disconnect', cleanup);
      call.on('cancel', cleanup);
      call.on('error', (e: any) => { setError(e?.message || 'Call error'); cleanup(); });
    } catch (e: any) {
      setError(e?.message || 'Could not start the call');
      cleanup();
    }
  };
  const hangUp = () => { try { callRef.current?.disconnect(); } catch { /* ignore */ } cleanup(); };
  const answer = () => { try { incomingRef.current?.accept(); callRef.current = incomingRef.current; setCallState('in-call'); } catch { /* ignore */ } };
  const decline = () => { try { incomingRef.current?.reject(); } catch { /* ignore */ } cleanup(); };
  const chooseMic = async (id: string) => { try { await (deviceRef.current?.audio as any)?.setInputDevice(id); setInId(id); } catch { /* ignore */ } };
  const chooseSpeaker = async (id: string) => {
    try {
      const a: any = deviceRef.current?.audio;
      await a?.speakerDevices?.set(id);
      a?.ringtoneDevices?.set?.(id);
      setOutId(id);
    } catch { /* ignore */ }
  };

  const value: VoiceCtx = { status, edge, callState, activeWho, error, inputs, outputs, inId, outId, outputSupported, placeCall, callJob, hangUp, answer, decline, chooseMic, chooseSpeaker };

  return (
    <Ctx.Provider value={value}>
      {children}
      {isStaff && callState !== 'idle' && <GlobalCallBar state={callState} who={activeWho} onAnswer={answer} onDecline={decline} onHangUp={hangUp} />}
    </Ctx.Provider>
  );
}

const teal = 'var(--color-accent)';
const red = 'var(--color-error)';
const barBtn: React.CSSProperties = { padding: 'var(--spacing-2) var(--spacing-3)', border: 'none', borderRadius: 'var(--radius-default)', fontWeight: 600, cursor: 'pointer', color: '#fff' };

/** Fixed banner shown on every page while a call is incoming/active. */
function GlobalCallBar({ state, who, onAnswer, onDecline, onHangUp }: { state: CallState; who: string; onAnswer: () => void; onDecline: () => void; onHangUp: () => void }) {
  return (
    <div style={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 2000, display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg, 0 6px 24px rgba(0,0,0,0.2))', padding: 'var(--spacing-2) var(--spacing-4)', maxWidth: '92vw' }}>
      {state === 'incoming' ? (
        <>
          <span>📞 Incoming call: <strong>{who}</strong></span>
          <button style={{ ...barBtn, background: teal }} onClick={onAnswer}>Answer</button>
          <button style={{ ...barBtn, background: red }} onClick={onDecline}>Decline</button>
        </>
      ) : (
        <>
          <span>{state === 'in-call' ? '🔊 On call' : state === 'ringing' ? '📲 Ringing' : '… Connecting'}: <strong>{who}</strong></span>
          <button style={{ ...barBtn, background: red }} onClick={onHangUp}>Hang up</button>
        </>
      )}
    </div>
  );
}
